import { computed, ComputedRef } from 'vue';
import { useOnceStore } from '@apps-once/store';
import { useSubscriptionStore } from '@apps-once/store/subscription';
import { usePlansStore } from '@apps-billing-shared/store/plans';
import {
  AIO_HR_ADD_ON_IDS,
  AIO_PAYROLL_ADD_ON_IDS,
  AIO_ELIGIBLE_COUNTRIES,
  ADD_ON_SUBSCRIPTION_TYPE,
  SUBSCRIPTION_CHANNEL,
} from '@apps-billing-shared/definitions';

// The aio_bundle route is gated on the global flag — change_plan_aio_bundle
// falls back to standard checkout when it is off (dp.my.desktop.js) — so every
// existing-user entrypoint that navigates there must respect it as well, or the
// CTA silently drops the tenant into standard checkout.
const AIO_FLAG = 'wo-3223-enable-aio-bundle';
const BUNDLE_FLAG = 'wo-3223-enable-aio-bundle-existing-users';
const PROMO_FLAG = 'growth-3606-enable-aio-existing-users-promo';
// Types that mean "the tenant has this add-on right now". CANCELLED_ACTIVE
// counts: the add-on runs to the end of the billing period, so offering a
// bundle that includes it would be offering something they already have.
// Matches getCurrentActiveAndCancelledActiveAddOns, which folds it into active.
const PAYING_TYPES = [
  ADD_ON_SUBSCRIPTION_TYPE.PAID_ACTIVE,
  ADD_ON_SUBSCRIPTION_TYPE.PRE_IMPLEMENTATION_ACTIVE,
  ADD_ON_SUBSCRIPTION_TYPE.ANNUAL_ACTIVE,
  ADD_ON_SUBSCRIPTION_TYPE.ANNUAL_INACTIVE,
  ADD_ON_SUBSCRIPTION_TYPE.CANCELLED_ACTIVE,
];

function isEligibleForBundle(): boolean {
  const onceStore = useOnceStore();
  return (
    onceStore.getFeatureFlag(AIO_FLAG) &&
    onceStore.getFeatureFlag(BUNDLE_FLAG) &&
    AIO_ELIGIBLE_COUNTRIES.includes(onceStore.country) &&
    // The backend sells the bundle to Direct tenants only. This also keeps out
    // CPQ tenants, who don't self-serve plan/add-on changes.
    onceStore.channel === SUBSCRIPTION_CHANNEL.DIRECT &&
    !onceStore.isAioBundle &&
    usePlansStore().isNewPlan(onceStore.plan) &&
    useSubscriptionStore().hasPaidSubscription(onceStore.hostname)
  );
}

function isPayingForFamily(ids: string[]): boolean {
  const onceStore = useOnceStore();
  return ids.some((id: string) =>
    PAYING_TYPES.includes(onceStore.getAddOnSubscriptionType(id))
  );
}

// The existing-customer promo runs from next month until the current term
// ends, so an annual tenant inside the renewal window has nothing left for it
// to discount. Monthly has no term end to be capped by.
// A plain function so the quote store can price the promo outside a component.
export function getAioExistingUserPromoBillingTerm(): 'monthly' | 'annual' | null {
  const onceStore = useOnceStore();
  const plansStore = usePlansStore();
  if (
    !isEligibleForBundle() ||
    (isPayingForFamily(AIO_HR_ADD_ON_IDS) && isPayingForFamily(AIO_PAYROLL_ADD_ON_IDS)) ||
    !onceStore.getFeatureFlag(PROMO_FLAG)
  ) {
    return null;
  }
  const isAnnual =
    plansStore.isAnnualUpfront(onceStore.plan) ||
    plansStore.isAnnualPaidMonthly(onceStore.plan);
  if (!isAnnual) {
    return 'monthly';
  }
  return onceStore.isAnnualSubscriptionEndingWithinNext31Days() ? null : 'annual';
}

export function useAioExistingUser(): {
  isEligible: ComputedRef<boolean>;
  isPayingForHr: ComputedRef<boolean>;
  isPayingForPayroll: ComputedRef<boolean>;
  isPayingForBoth: ComputedRef<boolean>;
  hasExactlyOneOfHrPayroll: ComputedRef<boolean>;
  promoBillingTerm: ComputedRef<'monthly' | 'annual' | null>;
} {
  const isEligible = computed<boolean>(isEligibleForBundle);
  const isPayingForHr = computed<boolean>(() => isPayingForFamily(AIO_HR_ADD_ON_IDS));
  const isPayingForPayroll = computed<boolean>(() => isPayingForFamily(AIO_PAYROLL_ADD_ON_IDS));
  const isPayingForBoth = computed<boolean>(() => isPayingForHr.value && isPayingForPayroll.value);
  const hasExactlyOneOfHrPayroll = computed<boolean>(
    () => isPayingForHr.value !== isPayingForPayroll.value
  );
  const promoBillingTerm = computed<'monthly' | 'annual' | null>(
    getAioExistingUserPromoBillingTerm
  );

  return {
    isEligible,
    isPayingForHr,
    isPayingForPayroll,
    isPayingForBoth,
    hasExactlyOneOfHrPayroll,
    promoBillingTerm,
  };
}
